import React, { useMemo, useState, useEffect, useContext } from 'react'
import classnames from "classnames";
import FEATURES from "../../config/features";
import { WorkOrderContext } from "../../providers/workorders.provider";

// Date range constants for stats, defaults to today
const getDateRangeParams = (filter) => {
  const start = new Date();
  const end = new Date();
  
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  if (filter === "tomorrow") {
    start.setDate(start.getDate() + 1);
    end.setDate(end.getDate() + 1);
  } else if (filter === "week") {
    end.setDate(start.getDate() + 6);
  }
  
  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0]
  };
};

function KpiTileCard({ label, subtitle, value, onClick, className }) {
  return (
    <div
      className={classnames(
        "rbr-card",
        className
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
    >
      <div className="card-heading uk-inline-item">{label}</div>
      <span className="small-notification">{subtitle || <>&nbsp;</>}</span>
      <div className="hms-blue-number">{value}</div>
    </div>
  );
}

function SectionRow({ title, start, end, children }) {
  return (
    <div className="uk-margin-large-bottom">
      <div className="uk-flex uk-flex-between uk-flex-middle">
        <h2 className="uk-margin-remove">{title}</h2>        
      </div>
      {children}
    </div>
  );
}

const MOCK_TASKS = [/* ... your same MOCK_TASKS array ... */];

const normalizeType = (t) => {
  const v = (t?.type || "").toLowerCase();
  if (["hk","housekeeping"].includes(v)) return "housekeeping";
  if (["in","inspection","inspections"].includes(v)) return "inspection";
  if (["mt","maintenance"].includes(v)) return "maintenance";
  if (["wo","work_order","workorders","work-order"].includes(v)) return "work_order";
  return v || "other";
};

const toDate = (t) => {
  // Try many possible fields
  const raw =
    t.dueAt ?? t.due_at ?? t.when ?? t.dueAtISO ??
    t.start_at ?? t.created_at ?? t.createdAt ?? null;

  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  // no usable date: treat as today so it survives window filtering
  const today = new Date();
  today.setHours(0,0,0,0);
  return today;
};

const inRange = (d, start, end) =>
  d.getTime() >= start.getTime() && d.getTime() <= end.getTime();

const collectProperties = (list) => {
  const out = [];
  for (const t of list) {
    if (Array.isArray(t.properties)) out.push(...t.properties);
    else if (t.property) out.push(t.property);
  }
  return Array.from(new Set(out));
};

const bucketTaskStatus = (s) => {
  switch ((s || "").toLowerCase()) {
    case "scheduled":
    case "paused": return "not_started";
    case "in_progress": return "in_progress";
    case "qc":
    case "awaiting_approval": return "resolved";
    case "complete":
    case "completed":
    case "closed":
    case "canceled": // remove this line if you do NOT want canceled to count as completed
      return "closed";
    default: return "not_started";
  }
};

const bucketWoStatus = (s) => {
  switch ((s || "").toLowerCase()) {
    case "open":
    case "scheduled":
    case "awaiting_approval": return "open";
    case "in_progress":       return "in_progress";
    case "qc":
    case "awaiting_parts":
    case "ready_to_close":    return "resolved";
    case "complete":
    case "completed":
    case "closed":
    case "canceled":
      return "closed";
    default: return "open";
  }
};

function useDateWindow(selectedFilter) {
  return React.useMemo(() => {
    const start = new Date();
    const end   = new Date();
    const toStart = d => d.setHours(0,0,0,0);
    const toEnd   = d => d.setHours(23,59,59,999);

    if (selectedFilter === "today") {
      toStart(start); toEnd(end);
    } else if (selectedFilter === "tomorrow") {
      start.setDate(start.getDate() + 1); toStart(start);
      end.setDate(end.getDate() + 1);     toEnd(end);
    } else { // "week" -> Next 7 Days starting today
      toStart(start);
      end.setDate(start.getDate() + 6);   toEnd(end);
    }
    return { start, end };
  }, [selectedFilter]);
}

const sumBucketsGeneric = (list, bucketFn, keys, closedKey = "closed") => {
  const res = Object.fromEntries(keys.map(k => [k, 0]));
  for (const t of list) {
    const b = bucketFn(t.status);
    if (res[b] === undefined) res[b] = 0; // guard against unexpected bucket
    res[b]++;
  }
  const total = Object.values(res).reduce((a, b) => a + b, 0);
  const pct = total ? Math.round((res[closedKey] / total) * 100) : 0;
  return { ...res, total, pct };
};

function useOpsKpis(allTasks, windowStart, windowEnd) {
  return React.useMemo(() => {
    const filtered = (allTasks || []).filter(t => {
      const hasAnyDate = !!(t.dueAt || t.due_at || t.when || t.dueAtISO || t.start_at || t.created_at || t.createdAt);
      return hasAnyDate ? inRange(toDate(t), windowStart, windowEnd) : true;
    });

    const byType = (type) => filtered.filter(t => normalizeType(t) === type);

    const hk = byType("housekeeping");
    const ins = byType("inspection");
    const mt = byType("maintenance");
    const wo = byType("work_order");

    const hkCounts  = sumBucketsGeneric(hk,  bucketTaskStatus, ["not_started","in_progress","resolved","closed"]);
    const insCounts = sumBucketsGeneric(ins, bucketTaskStatus, ["not_started","in_progress","resolved","closed"]);
    const mtCounts  = sumBucketsGeneric(mt,  bucketTaskStatus, ["not_started","in_progress","resolved","closed"]);
    const woCounts  = sumBucketsGeneric(wo,  bucketWoStatus,  ["open","in_progress","resolved","closed"], "closed");

    const collectProperties = (list) => {
      const out = [];
      for (const t of list) {
        if (Array.isArray(t.properties)) out.push(...t.properties);
        else if (t.property) out.push(t.property);
      }
      return Array.from(new Set(out));
    };

    const countSLA = (list) => {
      let ok = 0, risk = 0, breach = 0, unknown = 0;
      for (const t of list) {
        const s = (t.sla || "").toLowerCase();
        if (s === "ok") ok++; else if (s === "risk") risk++; else if (s === "breach") breach++; else unknown++;
      }
      return { ok, risk, breach, unknown };
    };

    return {
      housekeeping: { homes: collectProperties(hk).length,  ...hkCounts,  sla: countSLA(hk) },
      inspections:  { homes: collectProperties(ins).length, ...insCounts, sla: countSLA(ins) },
      maintenance:  { homes: collectProperties(mt).length,  ...mtCounts,  sla: countSLA(mt) },
      workOrders:   { ...woCounts, sla: countSLA(wo) }, // <-- has .open now
    };
  }, [allTasks, windowStart, windowEnd]);
}

export default function Dashboard({
  initialTasks = MOCK_TASKS,   // 🔁 was `tasks` prop; renamed to avoid clashing with local state
  onOpenTask,
}) {
  const { stats: workOrderStats, getStats: getWorkOrderStats } = useContext(WorkOrderContext);

  const [tasks, setTasks] = useState(initialTasks || []);  // safe default
  const [loading, setLoading] = useState(true);

  // filter UI
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [selectedFilter, setSelectedFilter] = useState("today");
  const [startDate, setStartDate] = useState(formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  useEffect(() => { document.title = "Ops Dashboard - RueBaRue"; }, []);

  useEffect(() => {
    const fetchWorkOrderStats = async () => {
      try {
        await getWorkOrderStats();
      } catch (error) {
        console.error('Error fetching work order stats:', error);
      }
    };

    fetchWorkOrderStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { start, end } = useDateWindow(selectedFilter);
  const kpis = useOpsKpis(tasks, start, end);

  const validateDates = () => {
    if (endDate && startDate && endDate < startDate) {
      alert("End date cannot be before start date.");
    }
  };

  return (
    <section id="dashboard-template">
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <h1 className="uk-heading-small uk-margin-remove">Ops Dashboard</h1>
      </div>
      <div id="hms-main-body">
        {/* TODO Phase 2: Date Filters */}
        {/* <div uk-grid="true" className="uk-margin-bottom">
          <div className="uk-button-group uk-margin-right">
            {["today","tomorrow","week"].map(f => (
              <button
                key={f}
                className={`uk-button dashboard-tab-button ${selectedFilter === f ? 'uk-button-primary' : 'uk-button-default'}`}
                onClick={() => setSelectedFilter(f)}
              >
                {f === "today" ? "Today" : f === "tomorrow" ? "Tomorrow" : "Next 7 Days"}
              </button>
            ))}
          </div>

          {selectedFilter === 'custom' && (
            <div className="uk-width-auto">
              <div className="uk-grid-small uk-margin-none-top" uk-grid="true">
                <div className="uk-width-1-2">
                  <input
                    className="uk-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); validateDates(); }}
                  />
                </div>
                <div className="uk-width-1-2">
                  <input
                    className="uk-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); validateDates(); }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

      <div className="uk-margin-small-top uk-margin-small-bottom">
        Showing: {start.toLocaleDateString()} – {end.toLocaleDateString()}
      </div> */}
      {FEATURES.HOUSEKEEPING_ENABLED && (
      <SectionRow title="Cleaning" start={start} end={end}>
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-5@m uk-margin-small-top" uk-grid="true">
            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="home"></span>
                    Number of Properties
                  </div>
                </div>
                <div className="hms-blue-number">
                  {kpis.housekeeping.homes}
                </div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="play-circle"></span>
                    Not Started
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.housekeeping.not_started}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="spinner"></span>
                    In Progress
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.housekeeping.in_progress}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    Completed
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.housekeeping.resolved}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    % Completed
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.housekeeping.pct}%</div>
              </div>
            </div>
          </div>
        </SectionRow>
      )}
        {/* Inspections */}
        {FEATURES.INSPECTIONS_ENABLED && (
        <SectionRow title="Inspections" start={start} end={end}>
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-5@m uk-margin-small-top" uk-grid="true">
            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="home"></span>
                    Number of Properties
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.inspections.homes}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="play-circle"></span>
                    Not Started
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.inspections.not_started}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="spinner"></span>
                    In Progress
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.inspections.in_progress}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    Resolved
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.inspections.resolved}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    % Completed
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.inspections.pct}%</div>
              </div>
            </div>
          </div>
        </SectionRow>
        )}

        {/* Maintenance */}
        {FEATURES.MAINTENANCE_ENABLED && (
        <SectionRow title="Maintenance" start={start} end={end}>
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-5@m uk-margin-small-top" uk-grid="true">
            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="home"></span>
                    Number of Properties
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.maintenance.homes}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="play-circle"></span>
                    Not Started
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.maintenance.not_started}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="spinner"></span>
                    In Progress
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.maintenance.in_progress}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    Resolved
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.maintenance.resolved}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    % Completed
                  </div>
                </div>
                <div className="hms-blue-number">{kpis.maintenance.pct}%</div>
              </div>
            </div>
          </div>
        </SectionRow>
        )}

        {/* Work Orders */}
        {FEATURES.WORK_ORDERS_OPS_DASHBOARD_ENABLED && (
        <SectionRow title="Work Orders" start={start} end={end}>
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-5@m uk-margin-small-top" uk-grid="true">
            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="file-text"></span>
                    New
                  </div>
                </div>
                <div className="hms-blue-number">{workOrderStats.new}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="clock"></span>
                    Started
                  </div>
                </div>
                <div className="hms-blue-number">{workOrderStats.started}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    Completed
                  </div>
                </div>
                <div className="hms-blue-number">{workOrderStats.completed}</div>
              </div>
            </div>

            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="archive"></span>
                    Closed
                  </div>
                </div>
                <div className="hms-blue-number">{workOrderStats.closed}</div>
              </div>
            </div>
            <div>
              <div className="rbr-card uk-margin-small-right">
                <div className="rbr-card-head">
                  <div className="rbr-card-title">
                    <span className="rbr-ico" uk-icon="check"></span>
                    % Completed
                  </div>
                </div>
                <div className="hms-blue-number">{Math.round(workOrderStats.percent_completed)}%</div>
              </div>
            </div>          

          </div>

        </SectionRow>
        )}
      </div>
    </section>
  );
}